/**
 * Moderação das páginas hospedadas.
 *
 * Qualquer pessoa autenticada publica aqui, e uma página pode ficar
 * aberta a **toda a companhia**, ou compartilhada com gente que não deveria
 * tê-la. Esta aba é onde isso se vê e, se precisar, se desfaz.
 *
 * ## O que mudou em 21/10
 *
 * O alcance anônimo (`PUBLICO`) acabou: nenhuma página sai da Iguatemi. A
 * urgência caiu junto, mas a aba não perdeu a razão de existir — mudou de
 * assunto, de "o que vaza para fora" para "o que alcança mais gente do que
 * deveria".
 *
 * ## A ordem da tela é a ordem do risco
 *
 * As de maior alcance vêm primeiro, e o filtro já abre nelas. Quem entra aqui
 * está procurando o que alcança muita gente; ordenar por data faria rolar
 * atrás do que importa.
 *
 * ## Olhar custa um registro, e a tela avisa
 *
 * Abrir o conteúdo de um projeto que não é seu grava `SITE_MODERATE` no
 * `access_log` — com quem olhou, de quem era e quando. Não é efeito colateral
 * escondido: o botão diz isso antes de ser clicado. O servidor não tem como
 * ser contornado (`podeVer` recusa administrador), então o aviso aqui é sobre
 * honestidade, não sobre controle.
 *
 * ## Fechar antes de apagar
 *
 * Fechar o alcance resolve o problema real — a página deixa de alcançar quem
 * não devia — e devolve o trabalho ao dono intacto. **Desde 21/10 ela leva
 * junto a lista de compartilhamento**, porque `COMPARTILHADO` é derivado de
 * haver gente na lista: fechar só a visibilidade devolveria o alcance no
 * instante seguinte. Apagar leva os arquivos e não
 * tem volta, e por isso é a ação secundária, atrás de uma confirmação.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch, SessionError, goToSignIn } from '../api.js';
import { RostosDoCartao } from './Rostos.jsx';
import { notify } from './Toast.jsx';

const VISIBILIDADE = {
  HUB: {
    label: 'Todo o AI Hub',
    cls: 'pending',
    risco: 'Qualquer pessoa com acesso ao AI Hub',
  },
  COMPARTILHADO: { label: 'Compartilhado', cls: 'info', risco: 'Só as pessoas da lista' },
  PRIVADO: { label: 'Privado', cls: 'neutral', risco: 'Só o dono' },
};

const FILTROS = [
  { id: 'HUB', label: 'Todo o AI Hub' },
  { id: 'COMPARTILHADO', label: 'Compartilhadas' },
  { id: 'PRIVADO', label: 'Privadas' },
  { id: '', label: 'Todas' },
];

const emKB = (b) => `${Math.max(1, Math.round((b ?? 0) / 1024))} KB`;
const dataCurta = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      })
    : '—';

/* ── a janela de examinar ────────────────────────────────────── */

function Exame({ site, onClose, onChanged }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [acao, setAcao] = useState(null); // 'despublicar' | 'apagar'
  const [motivo, setMotivo] = useState('');
  const [agindo, setAgindo] = useState(false);

  useEffect(() => {
    const aoTeclar = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onClose]);

  /**
   * Pedir para ver é o que grava o registro — por isso é `POST`, e por isso
   * só acontece depois de a pessoa clicar em Examinar, nunca ao montar a
   * lista.
   */
  useEffect(() => {
    let vivo = true;
    apiFetch(`/api/admin/sites/${encodeURIComponent(site.id)}/visualizacao`, { method: 'POST' })
      .then((d) => vivo && setDados(d))
      .catch((err) => {
        if (err instanceof SessionError) return goToSignIn();
        return vivo && setErro(err.message);
      });
    return () => {
      vivo = false;
    };
  }, [site.id]);

  async function executar() {
    setAgindo(true);
    try {
      const rota = acao === 'apagar' ? 'remocao' : 'despublicar';
      await apiFetch(`/api/admin/sites/${encodeURIComponent(site.id)}/${rota}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ reason: motivo.trim() }),
      });
      notify(acao === 'apagar' ? 'Página apagada.' : 'Página despublicada.');
      onChanged();
      onClose();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message, 'erro');
    } finally {
      setAgindo(false);
    }
    return undefined;
  }

  const v = VISIBILIDADE[site.visibility] ?? { label: site.visibility, cls: 'neutral' };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="item-panel editor-panel moderacao-painel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="moderar-titulo"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="panel-header editor-head">
          <div>
            <h2 id="moderar-titulo" className="panel-title">
              {site.name}
            </h2>
            <p className="panel-desc">
              {site.ownerUpn} · <span className={`status-pill ${v.cls}`}>{v.label}</span>
            </p>
          </div>
          <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        <div className="panel-body editor-body">
          {erro && (
            <div className="state-message error" role="alert">
              <p>{erro}</p>
            </div>
          )}

          {!dados && !erro && <p className="state-message">Abrindo…</p>}

          {dados && (
            <>
              {dados.logged && (
                <p className="moderacao-aviso" role="status">
                  Esta abertura ficou registrada no log de acesso, com o seu nome.
                </p>
              )}

              {dados.site.entryMissing || dados.files.length === 0 ? (
                <p className="state-message">
                  Esta página não abre — não há arquivo de entrada. Nada para examinar.
                </p>
              ) : (
                <iframe
                  className="site-preview"
                  title={`Conteúdo de ${site.name}`}
                  src={`${dados.contentBase}/${dados.site.entryPath
                    .split('/')
                    .map(encodeURIComponent)
                    .join('/')}`}
                  // A mesma contenção da página publicada. Moderar não é
                  // motivo para rodar conteúdo de usuário com mais permissão.
                  sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals"
                  referrerPolicy="no-referrer"
                />
              )}

              <div className="moderacao-fatos">
                <span>
                  {dados.files.length} arquivo{dados.files.length === 1 ? '' : 's'} ·{' '}
                  {emKB(site.totalBytes)}
                </span>
                <a href={site.path} target="_blank" rel="noreferrer">
                  Abrir em outra aba
                </a>
              </div>

              {!acao ? (
                <div className="panel-actions">
                  <button
                    type="button"
                    className="button secondary"
                    onClick={() => setAcao('despublicar')}
                    disabled={site.visibility === 'PRIVADO'}
                  >
                    {site.visibility === 'PRIVADO' ? 'Já está privada' : 'Fechar o alcance'}
                  </button>
                  <button type="button" className="button ghost" onClick={() => setAcao('apagar')}>
                    Apagar
                  </button>
                </div>
              ) : (
                <div className="site-danger" role="alert">
                  <p>
                    {acao === 'apagar' ? (
                      <>
                        <strong>Apagar leva os arquivos e não tem volta.</strong> Despublicar
                        resolve na maioria dos casos e devolve o trabalho ao dono.
                      </>
                    ) : (
                      <>
                        <strong>A página volta a ser privada.</strong> O dono continua com os
                        arquivos; o que acaba é o alcance.
                      </>
                    )}
                  </p>
                  <label className="field">
                    <span className="field-label">Motivo</span>
                    <input
                      className="input"
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                      placeholder="Fica no registro, e é o que o dono vai ler"
                      maxLength={500}
                      /* O campo só aparece depois de um clique deliberado em
                         Despublicar ou Apagar: o cursor vai para onde a pessoa
                         acabou de pedir para escrever. */
                      autoFocus
                    />
                  </label>
                  <div className="panel-actions">
                    <button
                      type="button"
                      className="button primary"
                      onClick={executar}
                      disabled={agindo || motivo.trim().length < 3}
                    >
                      {agindo ? 'Aplicando…' : acao === 'apagar' ? 'Apagar' : 'Fechar'}
                    </button>
                    <button
                      type="button"
                      className="button secondary"
                      onClick={() => setAcao(null)}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── a aba ───────────────────────────────────────────────────── */

export function AdminSites() {
  // Abre nas públicas: é o que alcança gente de fora.
  const [filtro, setFiltro] = useState('HUB');
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [examinando, setExaminando] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const q = filtro ? `?visibility=${encodeURIComponent(filtro)}` : '';
      setDados(await apiFetch(`/api/admin/sites${q}`));
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setErro(err.message);
    } finally {
      setCarregando(false);
    }
    return undefined;
  }, [filtro]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const lista = dados?.sites ?? [];
  const contagem = dados?.counts ?? {};

  return (
    <div className="list-panel">
      <div className="tabs-row">
        <div className="tabs" role="tablist" aria-label="Filtrar por visibilidade">
          {FILTROS.map((f) => (
            <button
              key={f.id || 'todas'}
              type="button"
              role="tab"
              aria-selected={filtro === f.id}
              className={`tab ${filtro === f.id ? 'is-active' : ''}`}
              onClick={() => setFiltro(f.id)}
            >
              {f.label}
              {f.id && contagem[f.id] !== undefined && (
                <span className="tab-badge">{contagem[f.id]}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {carregando && <p className="state-message">Carregando…</p>}

      {erro && (
        <div className="state-message error" role="alert">
          <p>{erro}</p>
          <button className="button secondary" type="button" onClick={carregar}>
            Tentar de novo
          </button>
        </div>
      )}

      {!carregando && !erro && lista.length === 0 && (
        <p className="state-message">
          {filtro === 'HUB'
            ? 'Nenhum artefato aberto a todo o AI Hub no momento.'
            : 'Nenhum artefato nesta visibilidade.'}
        </p>
      )}

      {!carregando && !erro && lista.length > 0 && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Página</th>
                <th>Publicou</th>
                <th>Quem vê</th>
                <th className="num">Arquivos</th>
                <th>Atualizado</th>
                <th className="col-action" />
              </tr>
            </thead>
            <tbody>
              {lista.map((s) => {
                const v = VISIBILIDADE[s.visibility] ?? { label: s.visibility, cls: 'neutral' };
                return (
                  <tr key={s.id}>
                    <td>
                      <div className="cell-main">{s.name}</div>
                      {s.description && <div className="cell-sub">{s.description}</div>}
                    </td>
                    <td>
                      <div className="cell-sub">{s.ownerUpn}</div>
                    </td>
                    <td>
                      <span className={`status-pill ${v.cls}`}>{v.label}</span>
                      {/* "Compartilhado" sem dizer COM QUEM é meia
                          informação numa tela de moderação: o que se avalia é
                          se o alcance faz sentido, e isso são os nomes. */}
                      {s.visibility === 'COMPARTILHADO' && s.shares?.length ? (
                        <div className="site-card-rostos">
                          <RostosDoCartao lista={s.shares} total={s.shares.length} />
                        </div>
                      ) : (
                        <div className="cell-sub">{v.risco}</div>
                      )}
                    </td>
                    <td className="num">
                      {s.fileCount}
                      <div className="cell-sub">{emKB(s.totalBytes)}</div>
                    </td>
                    <td>
                      <div className="cell-sub">{dataCurta(s.updatedAt)}</div>
                    </td>
                    <td className="col-action">
                      {/* O texto diz o preço antes do clique: abrir o conteúdo
                          de outra pessoa fica no log. */}
                      <button
                        type="button"
                        className="button secondary"
                        onClick={() => setExaminando(s)}
                        title="Abre o conteúdo e registra no log de acesso"
                      >
                        Examinar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {examinando && (
        <Exame site={examinando} onClose={() => setExaminando(null)} onChanged={carregar} />
      )}
    </div>
  );
}

export default AdminSites;
